import React, { useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { FaCheck, FaPencilAlt, FaPlus, FaTimes, FaTrashAlt } from 'react-icons/fa';

const CHAVE_GES = /^\s*\d+\s*\.\s*\d+\s*\.\s*\d+\s*$/;

// Valida um par NF+Chave nos mesmos moldes do backend (par atômico, NF sem "="/";" e
// até 50 caracteres, chave no formato GE.Portador.Sequência). Usada tanto ao "Adicionar"
// quanto ao confirmar (✅) uma edição inline — feedback imediato, sem esperar o submit
// do fechamento. A validação de duplicidade (R7b) é responsabilidade exclusiva do
// backend (TechSpec §3.4) — não é replicada aqui.
const validarPar = (numNf, chave) => {
  const nf = (numNf || '').trim();
  const chaveTrim = (chave || '').trim();

  if (!nf && !chaveTrim) return { valido: false, silencioso: true };
  if (!nf) return { valido: false, erro: 'Informe a NF adicional.' };
  if (!chaveTrim) return { valido: false, erro: 'Informe a chave adicional.' };
  if (nf.length > 50) return { valido: false, erro: 'NF adicional deve conter até 50 caracteres.' };
  if (/[=;]/.test(nf)) return { valido: false, erro: 'NF adicional não pode conter os caracteres = ou ;' };
  if (!CHAVE_GES.test(chaveTrim)) {
    return { valido: false, erro: 'Chave adicional deve estar no formato GE.Portador.Sequência (ex.: 1.237.45821).' };
  }

  return { valido: true, numNf: nf, chave: chaveTrim };
};

// DW-1063 (ADR-005) — modal dedicado "Notas Fiscais Adicionais". Componente 100% local:
// adicionar, editar (inline, ✅/❌) e remover só alteram o array `itens` recebido via
// callbacks do ModalNovoFechamento (pai) — nenhuma chamada de API aqui. A persistência
// real só ocorre quando o fechamento pai é salvo, com o array completo no payload.
const ModalNfAdicionais = ({ show, onHide, itens, onAdicionar, onEditar, onRemover }) => {
  const [novoNumNf, setNovoNumNf] = useState('');
  const [novaChave, setNovaChave] = useState('');
  const [erroNovo, setErroNovo] = useState(null);
  const [indiceEditando, setIndiceEditando] = useState(null);
  const [edicaoNumNf, setEdicaoNumNf] = useState('');
  const [edicaoChave, setEdicaoChave] = useState('');
  const [erroEdicao, setErroEdicao] = useState(null);

  const fecharComLimpeza = () => {
    setNovoNumNf('');
    setNovaChave('');
    setErroNovo(null);
    setIndiceEditando(null);
    setEdicaoNumNf('');
    setEdicaoChave('');
    setErroEdicao(null);
    onHide();
  };

  const confirmarAdicionar = () => {
    const resultado = validarPar(novoNumNf, novaChave);
    if (!resultado.valido) {
      if (!resultado.silencioso) setErroNovo(resultado.erro);
      return;
    }

    onAdicionar({ numNf: resultado.numNf, chave: resultado.chave });
    setNovoNumNf('');
    setNovaChave('');
    setErroNovo(null);
  };

  const iniciarEdicao = indice => {
    const item = itens[indice];
    setIndiceEditando(indice);
    setEdicaoNumNf(item.numNf);
    setEdicaoChave(item.chave);
    setErroEdicao(null);
  };

  const confirmarEdicao = () => {
    const resultado = validarPar(edicaoNumNf, edicaoChave);
    if (!resultado.valido) {
      setErroEdicao(resultado.erro || 'Informe a NF adicional e a chave adicional.');
      return;
    }

    onEditar(itens[indiceEditando].id, { numNf: resultado.numNf, chave: resultado.chave });
    setIndiceEditando(null);
    setErroEdicao(null);
  };

  const cancelarEdicao = () => {
    setIndiceEditando(null);
    setErroEdicao(null);
  };

  return (
    <Modal className="hpj-modal hpj-modal-nf-adicionais" show={show} onHide={fecharComLimpeza} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Notas Fiscais Adicionais</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="d-flex align-items-end mb-3">
          <div className="mr-2" style={{ flex: 1 }}>
            <Form.Control
              placeholder="NF Adicional"
              value={novoNumNf}
              onChange={event => setNovoNumNf(event.target.value)}
            />
          </div>
          <div className="mr-2" style={{ flex: 1 }}>
            <Form.Control
              placeholder="Chave Adicional (ex.: 1.237.45821)"
              value={novaChave}
              onChange={event => setNovaChave(event.target.value)}
            />
          </div>
          <Button className="hpj-btn hpj-btn-primary" variant="primary" type="button" onClick={confirmarAdicionar}>
            <FaPlus className="mr-2" /> Adicionar
          </Button>
        </Form.Group>
        {erroNovo && <div className="hpj-field-error mb-3">{erroNovo}</div>}

        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              <th>NF</th>
              <th>Chave</th>
              <th style={{ width: 120 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {itens.length === 0 && (
              <tr>
                <td colSpan={3} className="text-muted text-center">
                  Nenhuma NF adicional.
                </td>
              </tr>
            )}
            {itens.map((item, indice) => (
              <tr key={item.id}>
                {indiceEditando === indice ? (
                  <>
                    <td>
                      <Form.Control
                        size="sm"
                        value={edicaoNumNf}
                        onChange={event => setEdicaoNumNf(event.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={edicaoChave}
                        onChange={event => setEdicaoChave(event.target.value)}
                      />
                    </td>
                    <td>
                      <Button
                        className="hpj-btn mr-1"
                        size="sm"
                        variant="outline-success"
                        type="button"
                        title="Confirmar"
                        onClick={confirmarEdicao}
                      >
                        <FaCheck />
                      </Button>
                      <Button
                        className="hpj-btn"
                        size="sm"
                        variant="outline-secondary"
                        type="button"
                        title="Cancelar"
                        onClick={cancelarEdicao}
                      >
                        <FaTimes />
                      </Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{item.numNf}</td>
                    <td>{item.chave}</td>
                    <td>
                      <Button
                        className="hpj-btn mr-1"
                        size="sm"
                        variant="outline-primary"
                        type="button"
                        title="Editar"
                        onClick={() => iniciarEdicao(indice)}
                      >
                        <FaPencilAlt />
                      </Button>
                      <Button
                        className="hpj-btn hpj-btn-danger"
                        size="sm"
                        variant="outline-danger"
                        type="button"
                        title="Remover"
                        onClick={() => onRemover(item.id)}
                      >
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
        {indiceEditando !== null && erroEdicao && (
          <div className="hpj-field-error mb-2">{erroEdicao}</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="hpj-btn hpj-btn-primary" variant="primary" type="button" onClick={fecharComLimpeza}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalNfAdicionais;
